import { useState } from 'react';
import { motion } from 'framer-motion';
import { api } from '../lib/api';
import PanelBrandLockup from '../components/ui/PanelBrandLockup';
import { PublicPanelCard, PublicPanelShell } from '../components/ui/PublicPanelShell';

interface Props {
  onComplete: () => void;
  onSwitchToLogin: () => void;
}

export default function SignupPage({ onComplete, onSwitchToLogin }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password || !businessName) return;
    setLoading(true);
    setError(null);
    try {
      await api.auth.signup(email, password, businessName);
      onComplete();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <PublicPanelShell>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="w-full"
      >
        <div className="mb-8">
          <PanelBrandLockup
            title="Crea tu sitio"
            subtitle="Empezamos con tu negocio y tu acceso. Luego te ayudamos a armar el contenido inicial."
            compact
            minimal
          />
        </div>
        <PublicPanelCard>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1">
              <h2 className="text-xl font-semibold text-ui-text">Tu cuenta inicial</h2>
              <p className="text-sm text-ui-text-muted">
                En unos minutos tendrás tu panel listo para empezar a personalizarlo.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm text-ui-text-muted mb-1">¿Cómo se llama tu negocio?</label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  required
                  autoFocus
                  placeholder="Mi Empresa"
                  className="w-full bg-ui-bg border border-ui-border rounded-lg px-3 py-2 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent"
                />
              </div>
              <div>
                <label className="block text-sm text-ui-text-muted mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="tu@email.com"
                  className="w-full bg-ui-bg border border-ui-border rounded-lg px-3 py-2 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent"
                />
              </div>
              <div>
                <label className="block text-sm text-ui-text-muted mb-1">Contraseña</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="Mínimo 8 caracteres"
                  className="w-full bg-ui-bg border border-ui-border rounded-lg px-3 py-2 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent"
                />
              </div>
            </div>

            {error && <p className="text-sm text-ui-danger">{error}</p>}

            <motion.button
              type="submit"
              disabled={loading}
              whileTap={{ scale: 0.97 }}
              className="w-full px-4 py-2.5 bg-ui-accent text-ui-text-inverse text-sm font-medium rounded-lg disabled:opacity-50 hover:opacity-90 transition-opacity"
            >
              {loading ? 'Creando tu sitio…' : 'Empezar'}
            </motion.button>

            <p className="text-sm text-ui-text-subtle text-center">
              ¿Ya tienes cuenta?{' '}
              <button type="button" onClick={onSwitchToLogin} className="text-ui-accent hover:underline">
                Inicia sesión
              </button>
            </p>
          </form>
        </PublicPanelCard>
      </motion.div>
    </PublicPanelShell>
  );
}
